import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchCategoryByIdThunk } from "../store/slices/categoriesReducer";
import TableRowItem from "../components/TableRowItem";
import TableRowItemNew from "../components/TableRowItemNew";
import JourneyBreadcrumb from "../components/navigation/JourneyBreadcrumb";
import { useTranslation } from "../hooks/useTranslation";

const CategoryPage = () => {
  const { t, currentLanguage } = useTranslation();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Get category from Redux state
  const { category } = useSelector((state) => state.categories);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        dispatch(fetchCategoryByIdThunk(id));
        
        // The category response already contains the dishes for this category
        // We don't need to fetch all dishes and filter
      } catch (error) {
        console.error("Error loading data:", error);
        setDishes([]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dispatch, id]);

  // Update dishes when category data is loaded
  useEffect(() => {
    if (category && category.dishes) {
      console.log("Setting dishes from category:", category.dishes.length, "dishes");
      setDishes(category.dishes);
    }
  }, [category]);

  const handleDishDelete = (dishId) => {
    setDishes(dishes.filter(dish => dish.id !== dishId));
  };

  const handleDishUpdate = (dishId, newName) => {
    setDishes(dishes.map(dish => 
      dish.id === dishId ? { ...dish, name: newName } : dish
    ));
  };

  const handleDishCreated = (newDish) => {
    // Add the new dish to the current list
    setDishes(prevDishes => [...prevDishes, newDish]);
    // Also refresh the category data to ensure consistency
    dispatch(fetchCategoryByIdThunk(id));
  };

  const handleDishCopy = (newDishId, newDishName) => {
    // Add the new dish to the current list
    const newDish = {
      id: newDishId,
      name: newDishName,
      // Add other default properties that might be needed
      dish_and_ingredients_count: 0,
      climate_score: 0,
      water_score: 0,
      health_score: 0,
      social_justice_score: 0
    };
    setDishes(prevDishes => [...prevDishes, newDish]);
    // Also refresh the category data to ensure consistency
    dispatch(fetchCategoryByIdThunk(id));
  };

  // Helper function to calculate average sustainability score
  const calculateAverageSustainability = (dish) => {
    const scores = [
      dish.climate_score || 0,
      dish.water_score || 0,
      dish.health_score || 0,
      dish.social_justice_score || 0
    ];
    const validScores = scores.filter(score => score > 0);
    return validScores.length > 0 ? Math.round(validScores.reduce((sum, score) => sum + score, 0) / validScores.length) : 0;
  };

  // Filter dishes based on search term
  const filteredDishes = dishes.filter(dish =>
    dish.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const latestDishUpdate = dishes.reduce((latest, dish) => {
    const candidate = dish?.updated_at || dish?.created_at;
    if (!candidate) return latest;
    const candidateMs = new Date(candidate).getTime();
    if (Number.isNaN(candidateMs)) return latest;
    return candidateMs > latest ? candidateMs : latest;
  }, 0);

  const lastUpdatedLabel = latestDishUpdate
    ? new Intl.DateTimeFormat(currentLanguage === "sv" ? "sv-SE" : "en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(latestDishUpdate))
    : t("Ej angiven");

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-secondary-green border-t-transparent mx-auto"></div>
          <p className="mt-6 text-gray-600 text-lg font-medium">Laddar recept...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 tablet:px-6 laptop:px-8 desktop:px-8 py-8">
        <JourneyBreadcrumb
          items={[
            { label: t('Översikt'), to: "/" },
            { label: t(category?.name || 'Kategori') },
          ]}
          className="mb-6"
        />
        {/* Header */}
        <div className="mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
          <div className="flex items-center justify-between">
            <div>
              <Link 
                to="/menyer" 
                className="inline-flex items-center text-secondary-green hover:text-secondary-green/80 font-semibold transition-colors duration-200 mb-2"
              >
                <img 
                  src="/assets/icons/arrow_right.png" 
                  alt="Back" 
                  className="w-5 h-5 mr-2 transform rotate-180"
                />
                {t('Visa alla recept')}
                </Link>
              <h1 className="text-2xl mobile:text-3xl tablet:text-4xl laptop:text-4xl desktop:text-4xl font-bold text-gray-900 mb-2 mobile:mb-3 tablet:mb-3 laptop:mb-3 desktop:mb-3">{t(category?.name || 'Kategori')}</h1>
              <p className="text-sm mobile:text-base tablet:text-lg laptop:text-lg desktop:text-lg text-gray-600">
                {t('Hantera recept i denna kategori')}
              </p>
              {/* Category image under title */}
              {(() => {
                const directImage = category?.image_url || category?.image || null;
                const imgSrc = directImage ? String(directImage).replace(/\\\\/g, '/').replace(/\\/g, '/') : null;
                return imgSrc ? (
                  <div className="mt-4">
                    <div className="relative h-40 mobile:h-48 tablet:h-56 laptop:h-56 desktop:h-56 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl overflow-hidden">
                      <img 
                        src={imgSrc}
                        alt={category?.name || 'Kategori'}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="absolute inset-0 hidden items-center justify-center">
                        <img 
                          src="/assets/icons/Innehall.png" 
                          alt="Not found" 
                          className="w-12 h-12 text-gray-400"
                        />
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
            <button
              onClick={() => setAdding(!adding)}
              className={
                adding
                  ? "bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 mobile:py-3 tablet:py-3 laptop:py-3 desktop:py-3 px-4 mobile:px-6 tablet:px-6 laptop:px-6 desktop:px-6 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl border border-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 text-sm"
                  : "bg-gradient-to-r from-secondary-green to-secondary-green/90 hover:from-secondary-green/90 hover:to-secondary-green text-white font-semibold py-2 mobile:py-3 tablet:py-3 laptop:py-3 desktop:py-3 px-4 mobile:px-6 tablet:px-6 laptop:px-6 desktop:px-6 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-secondary-green focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] text-sm"
              }
            >
              {adding ? (
                <span className="inline-flex items-center">
                  <img 
                    src="/assets/icons/Dolj.png" 
                    alt="Cancel" 
                    className="w-5 h-5 mr-2"
                  />
                  {t('Avbryt')}
                </span>
              ) : (
                <span className="inline-flex items-center">
                  <img 
                    src="/assets/icons/Plus.png" 
                    alt="Add Recipe" 
                    className="w-5 h-5 mr-2"
                  />
                  {t('Lägg till recept')}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
          <div className="relative max-w-xs mobile:max-w-sm tablet:max-w-md laptop:max-w-md desktop:max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 mobile:pl-4 tablet:pl-4 laptop:pl-4 desktop:pl-4 flex items-center pointer-events-none">
              <img 
                src="/assets/icons/Filter.png" 
                alt="Search" 
                className="h-4 w-4 mobile:h-5 mobile:w-5 tablet:h-5 tablet:w-5 laptop:h-5 laptop:w-5 desktop:h-5 desktop:w-5 text-gray-400"
              />
            </div>
            <input
              type="text"
              placeholder={t('Sök recept...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 mobile:pl-12 tablet:pl-12 laptop:pl-12 desktop:pl-12 pr-4 py-2 mobile:py-3 tablet:py-3 laptop:py-3 desktop:py-3 border border-gray-300 rounded-lg mobile:rounded-xl tablet:rounded-xl laptop:rounded-xl desktop:rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary-green focus:border-transparent transition-all duration-200 bg-white shadow-sm text-sm mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm"
            />
          </div>
        </div>

        {/* Add New Recipe Form */}
          <TableRowItemNew 
            categoryId={id} 
            adding={adding} 
            setAdding={setAdding} 
            onDishCreated={handleDishCreated}
          />

        {/* Recipes List */}
        <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6 border-b border-gray-100">
            <h2 className="text-xl mobile:text-2xl tablet:text-2xl laptop:text-2xl desktop:text-2xl font-bold text-gray-900">{t('Recept')}</h2>
            <div className="flex items-center space-x-4">
              <span className="text-sm mobile:text-base tablet:text-base laptop:text-base desktop:text-base text-gray-600 lowercase">
                {searchTerm ? `${filteredDishes.length} av ${dishes.length}` : `${dishes.length}`} {t('Recept')}
              </span>
            </div>
        </div>

          {filteredDishes.length === 0 ? (
            <div className="text-center py-12 mobile:py-16 tablet:py-16 laptop:py-16 desktop:py-16">
              <div className="w-20 h-20 mobile:w-24 mobile:h-24 tablet:w-24 tablet:h-24 laptop:w-24 laptop:h-24 desktop:w-24 desktop:h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 mobile:mb-6 tablet:mb-6 laptop:mb-6 desktop:mb-6">
                <img 
                  src="/assets/icons/Innehall.png" 
                  alt="No Recipes" 
                  className="h-12 w-12 text-gray-400"
                />
              </div>
              <h3 className="text-lg mobile:text-xl tablet:text-xl laptop:text-xl desktop:text-xl font-semibold text-gray-900 mb-2">
                {searchTerm ? t('Inga recept hittades') : t('Inga recept än')}
              </h3>
              <p className="text-sm mobile:text-base tablet:text-base laptop:text-base desktop:text-base text-gray-600 mb-6 mobile:mb-8 tablet:mb-8 laptop:mb-8 desktop:mb-8">
                {searchTerm ? t('Prova att justera dina söktermer.') : t('Börja skapa ditt första recept i denna kategori.')}
              </p>
            </div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-fixed divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-center">
                    <th className="w-[34%] px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Recept')}
                  </th>
                    <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Genomsnittlig hållbarhet')}
                  </th>
                    <th className="px-6 py-4  text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Hållbarhetspoäng')}
                  </th>
                    <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('Åtgärder')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                  {filteredDishes.map((dish) => (
                    <TableRowItem key={dish.id} item={dish} onDelete={handleDishDelete} onUpdate={handleDishUpdate} onCopy={handleDishCopy} />
                  ))}
              </tbody>
            </table>
          </div>
          )}
        </div>

        {/* Sustainability Stats */}
        <div className="mt-8 mobile:mt-12 tablet:mt-12 laptop:mt-12 desktop:mt-12 grid grid-cols-1 mobile:grid-cols-2 tablet:grid-cols-4 laptop:grid-cols-4 desktop:grid-cols-4 gap-4 mobile:gap-6 tablet:gap-6 laptop:gap-6 desktop:gap-6">
          <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 mobile:w-16 mobile:h-16 tablet:w-16 tablet:h-16 laptop:w-16 laptop:h-16 desktop:w-16 desktop:h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl flex items-center justify-center">
                <img 
                  src="/assets/icons/Innehall.png" 
                  alt="Recipes Count" 
                  className="w-6 h-6 mobile:w-8 mobile:h-8 tablet:w-8 tablet:h-8 laptop:w-8 laptop:h-8 desktop:w-8 desktop:h-8 text-blue-600"
                />
              </div>
              <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500 mb-1">{t('Totalt antal recept')}</p>
                <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">{dishes.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 mobile:w-16 mobile:h-16 tablet:w-16 tablet:h-16 laptop:w-16 laptop:h-16 desktop:w-16 desktop:h-16 bg-gradient-to-br from-secondary-green/20 to-secondary-green/30 rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl flex items-center justify-center">
                <img 
                  src="/assets/icons/svg/Sustainability.svg" 
                  alt="Sustainability" 
                  className="w-10 h-10 mobile:w-10 mobile:h-10 tablet:w-10 tablet:h-10 laptop:w-10 laptop:h-10 desktop:w-10 desktop:h-10 text-secondary-green"
                />
              </div>
              <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500 mb-1">{t('Kategorihållbarhet')}</p>
                <p className="text-xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">
                  {dishes.length > 0 
                    ? Math.round(dishes.reduce((sum, dish) => sum + calculateAverageSustainability(dish), 0) / dishes.length)
                    : 0}/100
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 mobile:w-16 mobile:h-16 tablet:w-16 tablet:h-16 laptop:w-16 laptop:h-16 desktop:w-16 desktop:h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl flex items-center justify-center">
                <img 
                  src="/assets/icons/Innehall.png" 
                  alt="Ingredients" 
                  className="w-6 h-6 mobile:w-8 mobile:h-8 tablet:w-8 tablet:h-8 laptop:w-8 laptop:h-8 desktop:w-8 desktop:h-8 text-purple-600"
                />
              </div>
              <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500 mb-1">{t('Totalt antal ingredienser')}</p>
                <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900">
                  {dishes.reduce((sum, dish) => sum + (dish.dish_and_ingredients_count || 0), 0)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl shadow-sm border border-gray-100 p-4 mobile:p-6 tablet:p-6 laptop:p-6 desktop:p-6">
            <div className="flex items-center">
              <div className="w-12 h-12 mobile:w-16 mobile:h-16 tablet:w-16 tablet:h-16 laptop:w-16 laptop:h-16 desktop:w-16 desktop:h-16 bg-gradient-to-br from-yellow-100 to-yellow-200 rounded-xl mobile:rounded-2xl tablet:rounded-2xl laptop:rounded-2xl desktop:rounded-2xl flex items-center justify-center">
                <img 
                  src="/assets/icons/Innehall.png" 
                  alt="Last Updated" 
                  className="w-6 h-6 mobile:w-8 mobile:h-8 tablet:w-8 tablet:h-8 laptop:w-8 laptop:h-8 desktop:w-8 desktop:h-8 text-yellow-600"
                />
              </div>
              <div className="ml-3 mobile:ml-4 tablet:ml-4 laptop:ml-4 desktop:ml-4">
                <p className="text-xs mobile:text-sm tablet:text-sm laptop:text-sm desktop:text-sm font-medium text-gray-500 mb-1">{t('Senast uppdaterad')}</p>
                <p className="text-2xl mobile:text-3xl tablet:text-3xl laptop:text-3xl desktop:text-3xl font-bold text-gray-900"> {lastUpdatedLabel}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
